import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';

import firebaseConfig from '../firebase';
import type { Answer, LiveMode, LiveSession, Participant, QuizQuestion } from '../quiz/types';
import { normalizeJoinCode } from './normalizeJoinCode';

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);

const COL = {
  sessions: 'live_sessions',
  codes: 'live_sessions_by_pin',
  quizSets: 'live_quiz_sets'
};

function nowMs(): number {
  return Date.now();
}

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomJoinCode6(): string {
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return out;
}

async function sleep(ms: number): Promise<void> {
  await new Promise((r) => setTimeout(r, ms));
}

export async function ensureAnonymousAuth(): Promise<string> {
  const auth = getAuth(app);
  if (auth.currentUser) return auth.currentUser.uid;
  try {
    const cred = await signInAnonymously(auth);
    return cred.user.uid;
  } catch (err: any) {
    // In produzione, se il provider Anonimo non è abilitato in Firebase Auth,
    // signInAnonymously fallisce con 400 su identitytoolkit (OPERATION_NOT_ALLOWED).
    const code = String(err?.code || '');
    const message = String(err?.message || '');
    if (code === 'auth/operation-not-allowed' || message.includes('OPERATION_NOT_ALLOWED')) {
      throw new Error('ANON_AUTH_DISABLED');
    }
    throw err;
  }
}

export type QuizLiveSessionMode = 'presentation' | 'quiz';

export type QuizRef =
  | { type: 'deck'; deckId: string }
  | { type: 'quiz'; quizId: string };

export async function createQuizSet(params: {
  ownerUserId: string;
  title?: string;
  questions: Array<{
    id: string;
    type: 'mcq' | 'truefalse';
    prompt: string;
    options: Array<{ id: string; text: string }>;
    correctOptionId: string;
    explanation?: string;
    accessibility?: any;
  }>;
}): Promise<{ quizId: string }> {
  const ref = doc(collection(db, COL.quizSets));
  await setDoc(
    ref,
    {
      ownerUserId: params.ownerUserId,
      title: params.title || 'Quiz Live',
      questions: params.questions,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    } as any,
    { merge: false }
  );
  return { quizId: ref.id };
}

export async function listMyQuizSets(params: {
  ownerUserId: string;
  limit?: number;
}): Promise<Array<{ id: string; title: string }>> {
  const q = query(
    collection(db, COL.quizSets),
    where('ownerUserId', '==', params.ownerUserId),
    orderBy('updatedAt', 'desc'),
    limit(params.limit ?? 20)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return { id: d.id, title: String(data?.title || 'Quiz Live') };
  });
}

export async function getQuizSet(params: {
  quizId: string;
}): Promise<{ id: string; title: string; questions: QuizQuestion[] }> {
  const ref = doc(db, COL.quizSets, params.quizId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('QUIZ_SET_NOT_FOUND');
  const data = snap.data() as any;

  const questionsRaw: any[] = Array.isArray(data?.questions) ? data.questions : [];
  const questions: QuizQuestion[] = questionsRaw.map((q) => ({
    id: String(q?.id || ''),
    slideId: 'standalone',
    type: (q?.type || 'mcq') as any,
    prompt: String(q?.prompt || ''),
    options: Array.isArray(q?.options)
      ? q.options.map((o: any) => ({ id: String(o?.id || ''), text: String(o?.text || '') }))
      : [],
    correctOptionId: String(q?.correctOptionId || ''),
    explanation: q?.explanation ? String(q.explanation) : undefined,
    difficulty: 'base',
    accessibility: q?.accessibility || undefined
  }));

  return { id: snap.id, title: String(data?.title || 'Quiz Live'), questions };
}

export async function createLiveSession(params: {
  hostUserId: string;
  mode: QuizLiveSessionMode;
  quizRef: QuizRef;
  liveMode: LiveMode;
  showLeaderboard: boolean;
  inclusive: boolean;
  defaultTimeSec: number;
  expiresInHours?: number;
}): Promise<{ sessionId: string; joinCode: string }> {
  const expiresInHours = params.expiresInHours ?? 6;
  const expiresAt = nowMs() + expiresInHours * 60 * 60 * 1000;

  // Try a few times to avoid joinCode collisions.
  for (let attempt = 0; attempt < 10; attempt++) {
    const joinCode = randomJoinCode6();
    const normalizedJoinCode = normalizeJoinCode(joinCode);
    const codeRef = doc(db, COL.codes, normalizedJoinCode);

    try {
      const result = await runTransaction(db, async (tx) => {
        const existing = await tx.get(codeRef);
        if (existing.exists()) throw new Error('JOIN_CODE_COLLISION');

        const sessionRef = doc(collection(db, COL.sessions));

        // Back-compat fields: keep `pin` and `deckId` optional.
        const session: Omit<LiveSession, 'id'> & any = {
          hostUserId: params.hostUserId,
          pin: normalizedJoinCode,
          joinCode,
          normalizedJoinCode,
          status: 'lobby',
          createdAt: nowMs(),
          expiresAt,
          inclusive: params.inclusive,
          defaultTimeSec: params.defaultTimeSec,
          // new fields
          sessionMode: params.mode,
          quizRef: params.quizRef,
          current: {
            slideIndex: 0,
            mode: params.liveMode,
            showLeaderboard: params.inclusive ? false : params.showLeaderboard
          }
        };

        if (params.quizRef.type === 'deck') {
          session.deckId = params.quizRef.deckId;
        }

        tx.set(sessionRef, { ...session, createdAt: serverTimestamp() } as any);
        tx.set(
          codeRef,
          {
            sessionId: sessionRef.id,
            hostUserId: params.hostUserId,
            createdAt: serverTimestamp(),
            expiresAt,
            joinCode,
            normalizedJoinCode
          } as any
        );

        return { sessionId: sessionRef.id, joinCode };
      });

      return result;
    } catch (e) {
      const msg = String((e as any)?.message || '');
      if (msg.includes('JOIN_CODE_COLLISION')) continue;
    }
  }

  throw new Error('Impossibile generare un codice univoco');
}

export async function resolveSessionIdByCode(codeInput: string, opts?: { retries?: number }): Promise<string | null> {
  const normalized = normalizeJoinCode(codeInput);
  if (!/^[A-Z0-9]{6}$/.test(normalized)) return null;

  const retries = opts?.retries ?? 3;
  const delays = [300, 700, 1500];

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const ref = doc(db, COL.codes, normalized);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as any;
        return typeof data?.sessionId === 'string' ? data.sessionId : null;
      }
    } catch {
      // ignore and retry
    }

    if (attempt < retries) {
      await sleep(delays[Math.min(attempt, delays.length - 1)]);
    }
  }

  // Fallback legacy: if someone used raw joinCode in docId by mistake
  try {
    const rawTrim = String(codeInput || '').trim();
    const ref2 = doc(db, COL.codes, rawTrim);
    const snap2 = await getDoc(ref2);
    if (snap2.exists()) {
      const data = snap2.data() as any;
      return typeof data?.sessionId === 'string' ? data.sessionId : null;
    }
  } catch {
    // ignore
  }

  return null;
}

export function listenLiveSession(sessionId: string, cb: (session: LiveSession | null) => void): () => void {
  const ref = doc(db, COL.sessions, sessionId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        cb(null);
        return;
      }
      const data = snap.data() as any;
      cb({ id: snap.id, ...(data as any) } as LiveSession);
    },
    () => cb(null)
  );
}

export function listenParticipants(sessionId: string, cb: (participants: Participant[]) => void): () => void {
  const ref = collection(db, COL.sessions, sessionId, 'participants');
  // NOTE: non usiamo orderBy composito (score desc + joinedAt asc) per evitare di
  // dipendere da un indice Firestore composito mai creato (che faceva fallire la
  // classifica in modo silenzioso). Ordiniamo lato client.
  return onSnapshot(
    ref,
    (snap) => {
      const rows: Participant[] = snap.docs.map((d) => {
        const data = d.data() as any;
        return { id: d.id, ...(data as any) } as Participant;
      });
      rows.sort((a: any, b: any) => {
        const sa = Number(a?.score || 0);
        const sb = Number(b?.score || 0);
        if (sb !== sa) return sb - sa;
        const ja = Number(a?.joinedAt?.toMillis?.() ?? a?.joinedAt ?? 0);
        const jb = Number(b?.joinedAt?.toMillis?.() ?? b?.joinedAt ?? 0);
        return ja - jb;
      });
      cb(rows);
    },
    () => cb([])
  );
}

export async function joinSessionAsParticipant(params: {
  sessionId: string;
  nickname?: string;
  isAnonymous: boolean;
}): Promise<string> {
  const uid = await ensureAnonymousAuth();
  const participantId = uid;

  const participantRef = doc(db, COL.sessions, params.sessionId, 'participants', participantId);
  const participant: Participant = {
    id: participantId,
    sessionId: params.sessionId,
    nickname: params.nickname ? params.nickname.trim().slice(0, 20) : undefined,
    isAnonymous: params.isAnonymous,
    joinedAt: nowMs(),
    score: 0
  };

  await setDoc(participantRef, { ...participant, joinedAt: serverTimestamp() } as any, { merge: true });
  return participantId;
}

export async function submitAnswer(params: {
  sessionId: string;
  questionId: string;
  participantId: string;
  optionId: string;
  answeredAt: number;
  timeMs?: number;
}): Promise<void> {
  const answerId = `${params.questionId}_${params.participantId}`;
  const ref = doc(db, COL.sessions, params.sessionId, 'answers', answerId);

  const answer: Answer = {
    id: answerId,
    sessionId: params.sessionId,
    questionId: params.questionId,
    participantId: params.participantId,
    optionId: params.optionId,
    answeredAt: params.answeredAt,
    timeMs: params.timeMs
  };

  await setDoc(ref, { ...answer, answeredAt: serverTimestamp() } as any, { merge: false });
}

export async function startLiveQuestion(params: {
  sessionId: string;
  slideIndex: number;
  question: QuizQuestion;
  durationSec: number;
  mode: LiveMode;
  showLeaderboard: boolean;
  inclusive: boolean;
}): Promise<void> {
  const durationSec = Math.max(5, Number(params.durationSec || 0));
  const ref = doc(db, COL.sessions, params.sessionId);

  await updateDoc(ref, {
    status: 'question',
    inclusive: params.inclusive,
    'current.slideIndex': params.slideIndex,
    'current.mode': params.mode,
    'current.showLeaderboard': params.inclusive ? false : params.showLeaderboard,
    // Prefer server-anchored timing to avoid host/student clock skew.
    'current.startedAt': serverTimestamp(),
    'current.durationSec': durationSec,
    // Keep legacy field for back-compat (some UIs may rely on it).
    'current.endsAt': nowMs() + durationSec * 1000,
    'current.results': null,
    'current.question': {
      id: params.question.id,
      slideId: params.question.slideId,
      type: params.question.type,
      prompt: params.question.prompt,
      options: params.question.options,
      accessibility: params.question.accessibility || null
    }
  } as any);
}

export async function finalizeLiveResults(params: {
  sessionId: string;
  question: QuizQuestion;
  collectivePickedOptionId?: string;
}): Promise<void> {
  const sessionRef = doc(db, COL.sessions, params.sessionId);

  const answersRef = collection(db, COL.sessions, params.sessionId, 'answers');
  const qs = query(answersRef, where('questionId', '==', params.question.id));
  const snap = await getDocs(qs);

  const counts: Record<string, number> = {};
  snap.docs.forEach((d) => {
    const a = d.data() as any;
    const opt = String(a?.optionId || '');
    if (!opt) return;
    counts[opt] = (counts[opt] || 0) + 1;
  });

  await Promise.all(
    snap.docs.map(async (d) => {
      const a = d.data() as any;
      const opt = String(a?.optionId || '');
      const isCorrect = opt === params.question.correctOptionId;
      await updateDoc(d.ref, {
        isCorrect,
        scoreDelta: isCorrect ? 1 : 0
      } as any).catch(() => undefined);

      if (isCorrect) {
        const pRef = doc(db, COL.sessions, params.sessionId, 'participants', String(a?.participantId || ''));
        await updateDoc(pRef, { score: increment(1) } as any).catch(() => undefined);
      }
    })
  );

  await updateDoc(sessionRef, {
    status: 'results',
    'current.results': {
      correctOptionId: params.question.correctOptionId,
      explanation: params.question.explanation || null,
      countsByOptionId: counts,
      totalAnswers: snap.size,
      collectivePickedOptionId: params.collectivePickedOptionId || null
    }
  } as any);
}

export async function goToLobby(sessionId: string): Promise<void> {
  const ref = doc(db, COL.sessions, sessionId);
  await updateDoc(ref, {
    status: 'lobby',
    'current.question': null,
    'current.endsAt': null,
    'current.startedAt': null,
    'current.durationSec': null,
    'current.results': null
  } as any);
}

export async function endSession(sessionId: string): Promise<void> {
  const ref = doc(db, COL.sessions, sessionId);
  await updateDoc(ref, { status: 'ended' } as any);
}
